import type { RequestFn } from '../http';

/**
 * Statistik-Übersicht — aggregierte Kennzahlen über alle Arbeitsbereiche
 * (Tickets, Zeiterfassung, Rückrufe, KIAN, Telefonie, Reports, Übergaben, Material).
 */

export type StatsOverviewPeriod = 'last_24h' | 'this_month' | 'last_30_days' | 'all';

export interface TechnicianStatsRow {
  userId: string;
  name: string;
  openTickets: number;
  closedInPeriod: number;
  hours: number;
  /** Weclapp-Zeiten ohne Ticketbezug (Projekt-/freie Buchungen). */
  ticketlessHours: number;
  callbacksHandled: number;
  reportsSubmitted: number;
}

export interface StatsOverview {
  period: StatsOverviewPeriod;
  from: string | null;
  to: string;
  tickets: {
    openNow: {
      total: number;
      unassigned: number;
      /** Fälligkeitsdatum (dueDate) abgelaufen. */
      overdue: number;
      /** SLA-Lösungszeit gerissen (Kunden mit gültigem SLA, Näherung ohne pauseOnWaiting). */
      overdueSla: number;
      /** Offener Bestand aufgeschlüsselt nach Ticket-Status (Weclapp-Sortierung). */
      byStatus: Array<{ statusId: string | null; name: string; count: number }>;
    };
    createdInPeriod: number;
    closedInPeriod: number;
    byDay: Array<{ day: string; created: number; closed: number }>;
  };
  time: {
    hours: number;
    entries: number;
    /** Weclapp-Zeiten ohne Ticketbezug (Projekt-/freie Buchungen). */
    ticketless: { hours: number; entries: number };
    portalBookings: { count: number; minutes: number; billableMinutes: number };
  };
  callbacks: {
    newInPeriod: number;
    handledInPeriod: number;
    openNow: number;
    overdueNow: number;
  };
  kian: {
    callsInPeriod: number;
    matched: number;
    ticketsCreated: number;
    failed: number;
  };
  webex: {
    inbound: number;
    outbound: number;
    inboundAnswered: number;
    durationMinutes: number;
  };
  reports: { success: number; pending: number; failed: number; drafts: number };
  handovers: {
    createdInPeriod: number;
    accepted: number;
    declined: number;
    pendingNow: number;
  };
  material: { createdInPeriod: number; pendingNow: number; orderedInPeriod: number };
  /** Nur für ADMIN/TEAMLEAD gefüllt. */
  perTechnician: TechnicianStatsRow[] | null;
  /** Eigene Zeile des angemeldeten Users (falls er Aktivität hat). */
  me: TechnicianStatsRow | null;
}

/** Umfang des Zeiterfassungs-Drill-downs: mit oder ohne Ticketbezug. */
export type StatsTimeEntriesScope = 'ticket' | 'ticketless';

export interface StatsTimeEntry {
  id: string;
  ticketId: string | null;
  taskId: string | null;
  ticketNumber: string | null;
  ticketSubject: string | null;
  comment: string | null;
  userName: string | null;
  hours: number | null;
  startDate: string | null;
  /**
   * Direktlink in die Weclapp-Oberfläche. Die Buchung selbst hat dort keine eigene
   * Adresse — der Link zeigt auf die Aufgabe („Buchungsberichte"), ersatzweise aufs
   * Ticket. null, wenn keine Weclapp-Instanz konfiguriert ist.
   */
  weclappUrl: string | null;
  /**
   * Wer für wen gebucht hat (v1.404.0) — nur bei Buchungen für andere, sonst null. Steht nur im
   * ServiceHub, nie auf Leistungsnachweis oder Rechnung.
   */
  origin: StatsTimeEntryOrigin | null;
}

export interface StatsTimeEntryOrigin {
  /** „gebucht von X für Y" bzw. „Vorschlag von X, freigegeben von Y" */
  label: string | null;
  bookedByName: string | null;
  technicianName: string | null;
  viaProposal: boolean;
  approvedByName: string | null;
  approvedAt: string | null;
  source: string | null;
}

/** Filter der Zeiteinträge: alle oder nur die, die jemand für einen anderen gebucht/vorgeschlagen hat. */
export type StatsTimeEntriesOrigin = 'all' | 'others';

export interface StatsTimeEntriesResponse {
  period: StatsOverviewPeriod;
  scope: StatsTimeEntriesScope;
  origin?: StatsTimeEntriesOrigin;
  /** true = Techniker-Sicht, es werden nur die eigenen Einträge geliefert. */
  selfOnly: boolean;
  /** true = am Benutzer fehlt das Weclapp-Mapping, deshalb keine Einträge. */
  weclappUserMissing: boolean;
  /** Gesetzt, wenn auf einen Techniker gefiltert wurde (Drill-down aus der Team-Tabelle). */
  userName: string | null;
  from: string | null;
  to: string;
  /** Gesamtanzahl im Zeitraum — kann größer sein als entries.length (Limit 500). */
  total: number;
  entries: StatsTimeEntry[];
}

export function createStatsApi(request: RequestFn) {
  return {
    async getStatsOverview(period: StatsOverviewPeriod) {
      return request<StatsOverview>(`/stats/overview?period=${period}`);
    },
    async getStatsTimeEntries(
      period: StatsOverviewPeriod,
      scope: StatsTimeEntriesScope,
      /** Interne User.id — Drill-down auf einen Techniker (nur ADMIN/TEAMLEAD). */
      userId?: string,
      /** `others`: nur Buchungen für andere (gebucht oder über Zeitvorschlag) */
      origin: StatsTimeEntriesOrigin = 'all',
    ) {
      const q = new URLSearchParams({ period, scope });
      if (userId) q.set('userId', userId);
      if (origin === 'others') q.set('origin', 'others');
      return request<StatsTimeEntriesResponse>(`/stats/time-entries?${q.toString()}`);
    },
  };
}
